import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Shield, Warning, Check, Sparkle,
  Desktop, Lightning, Lock, ChartLineUp, Bell,
  ClipboardText, Brain, Users, Clock, Rocket,
  ArrowUpRight, Eye, Heartbeat, Wrench, House
} from "@phosphor-icons/react";

const ProductsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const products = [
    {
      id: "hospiceguard",
      name: "HospiceGuard",
      tagline: "Mobile Hospice Audit System",
      description: "HIPAA-compliant mobile audit solution with AI-powered documentation review. Conduct thorough mock audits, track compliance in real-time, and ensure survey readiness from anywhere.",
      website: "https://goodwinguard.com",
      icon: Shield,
      gradient: "from-cyan-500 to-blue-600",
      features: [
        "AI-powered documentation analysis",
        "Mobile audit capabilities",
        "Real-time compliance tracking",
        "CHAP & CMS CoP alignment",
        "Automated deficiency detection",
        "Corrective action planning"
      ],
      benefits: [
        { icon: Desktop, text: "Access audits from any device" },
        { icon: Lightning, text: "Instant compliance insights" },
        { icon: Lock, text: "HIPAA-compliant security" },
        { icon: ChartLineUp, text: "Track improvement trends" }
      ],
      cta: "Start Your Free Trial"
    },
    {
      id: "incidentinsight",
      name: "Incident Insight Pro",
      tagline: "AI-Powered Incident Reporting & Analysis",
      description: "HIPAA-compliant hospice incident reporting system with automated Root Cause Analysis. Streamline incident documentation, receive intelligent notifications, and transform incidents into quality improvement opportunities.",
      website: "https://incidentpath.com",
      icon: Warning,
      gradient: "from-orange-500 to-red-500",
      features: [
        "AI-powered Root Cause Analysis",
        "Automated notifications & follow-ups",
        "Two-factor authentication",
        "QAPI integration",
        "Trend analysis & reporting",
        "Customizable incident categories"
      ],
      benefits: [
        { icon: Brain, text: "Smart incident analysis" },
        { icon: Bell, text: "Automated alerts & follow-ups" },
        { icon: ClipboardText, text: "Streamlined documentation" },
        { icon: Users, text: "Team collaboration tools" }
      ],
      cta: "View Pricing Plans"
    },
    {
      id: "hospicecarelens",
      name: "HospiceCareLens",
      tagline: "Operational Intelligence Platform",
      description: "Comprehensive operational visibility for hospice leadership. Real-time dashboards, predictive analytics, and executive insights that transform data into actionable decisions for census growth, staff optimization, and quality improvement.",
      website: "https://hospicecarelens.com",
      icon: Eye,
      gradient: "from-indigo-500 to-purple-600",
      features: [
        "Executive intelligence dashboards",
        "Real-time census & productivity tracking",
        "Predictive staffing analytics",
        "Financial performance metrics",
        "Quality outcome visualization",
        "Automated leadership reports"
      ],
      benefits: [
        { icon: Eye, text: "360° operational visibility" },
        { icon: ChartLineUp, text: "Data-driven decisions" },
        { icon: Lightning, text: "Real-time insights" },
        { icon: Brain, text: "Predictive analytics" }
      ],
      cta: "Request a Demo"
    },
    {
      id: "homehealth-ops",
      name: "Home Health Operational System",
      tagline: "End-to-End Home Health Management",
      description: "Purpose-built operational system for home health agencies. Streamline scheduling, optimize visit routes, manage referrals, and track productivity—all designed specifically for home health workflows and compliance requirements.",
      website: null,
      icon: House,
      gradient: "from-emerald-500 to-teal-600",
      features: [
        "Intelligent scheduling & routing",
        "Referral management automation",
        "Visit productivity optimization",
        "OASIS documentation support",
        "Billing & revenue cycle tools",
        "Staff performance tracking"
      ],
      benefits: [
        { icon: Clock, text: "Reduce scheduling time by 50%" },
        { icon: Rocket, text: "Faster referral-to-admission" },
        { icon: Users, text: "Optimized staff utilization" },
        { icon: ChartLineUp, text: "Improved margins" }
      ],
      cta: "Schedule Consultation"
    }
  ];

  const customSystems = {
    title: "Custom System Development",
    tagline: "Built to Fit Your Workflow",
    description: "Every hospice and home health organization has unique workflows, challenges, and goals. Our team designs and builds custom operational systems tailored specifically to how your organization works—not the other way around.",
    capabilities: [
      "Custom dashboard development",
      "Workflow automation design",
      "Integration with existing EMR/EHR systems",
      "Proprietary reporting solutions",
      "Mobile-first applications",
      "AI-powered process optimization"
    ]
  };

  const integrationBenefits = [
    {
      icon: Clock,
      title: "Save 10+ Hours Weekly",
      description: "Automate manual compliance tasks and incident documentation"
    },
    {
      icon: Shield,
      title: "Zero Survey Deficiencies",
      description: "Proactive compliance monitoring catches issues before surveyors do"
    },
    {
      icon: ChartLineUp,
      title: "Data-Driven QAPI",
      description: "Transform incidents and audits into actionable quality improvements"
    },
    {
      icon: Lock,
      title: "Enterprise Security",
      description: "HIPAA-compliant with two-factor authentication and encryption"
    }
  ];

  return (
    <div data-testid="products-page" className="overflow-hidden">
      {/* Hero Section - Futuristic */}
      <section className="section-padding bg-slate-50 relative overflow-hidden" data-testid="products-hero">
        <div className="absolute inset-0 bg-mesh-gradient" />
        <div className="absolute inset-0 decorative-grid opacity-30" />
        
        <motion.div 
          className="absolute top-20 right-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="max-w-7xl mx-auto container-padding relative">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="badge badge-accent mb-8">
              <Rocket weight="fill" className="w-4 h-4 mr-2" />
              Our Technology Products
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-slate-900 mb-8 leading-[1.05] tracking-tight">
              AI-Powered Tools for{" "}
              <span className="text-gradient">Hospice Excellence</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Purpose-built software solutions designed by hospice compliance experts. 
              Streamline audits, automate incident reporting, and maintain survey readiness.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Section - Modern Cards */}
      <section className="section-padding bg-white" data-testid="products-list">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="space-y-16">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                data-testid={`product-${product.id}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r ${product.gradient} text-white mb-6`}>
                    <product.icon weight="fill" className="w-5 h-5" />
                    <span className="font-semibold text-sm">{product.name}</span>
                  </div>
                  
                  <h2 className="font-display text-3xl md:text-4xl text-slate-900 mb-4 tracking-tight">
                    {product.tagline}
                  </h2>
                  
                  <p className="text-lg text-slate-600 leading-relaxed mb-8">
                    {product.description}
                  </p>

                  {/* Features Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {product.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check weight="bold" className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-wrap gap-4">
                    {product.website ? (
                      <a
                        href={product.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-accent inline-flex items-center gap-2"
                        data-testid={`${product.id}-cta`}
                      >
                        {product.cta}
                        <ArrowUpRight weight="bold" className="w-5 h-5" />
                      </a>
                    ) : (
                      <Link
                        to="/schedule-demo"
                        className="btn-accent inline-flex items-center gap-2"
                        data-testid={`${product.id}-cta`}
                      >
                        {product.cta}
                        <ArrowRight weight="bold" className="w-5 h-5" />
                      </Link>
                    )}
                    <Link
                      to="/schedule-demo"
                      className="btn-secondary inline-flex items-center gap-2"
                    >
                      Schedule Demo
                    </Link>
                  </div>
                </div>

                {/* Visual Card */}
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="relative rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
                    <div className={`absolute inset-0 bg-gradient-to-r ${product.gradient} opacity-10`} />
                    
                    {/* Decorative circles */}
                    <div className="absolute top-6 right-6 w-24 h-24 border border-cyan-500/20 rounded-full" />
                    <div className="absolute bottom-6 left-6 w-32 h-32 border border-cyan-500/20 rounded-full" />
                    
                    <div className="relative p-8 md:p-12">
                      {/* Product Icon */}
                      <div className={`w-20 h-20 rounded-2xl bg-gradient-to-r ${product.gradient} flex items-center justify-center mx-auto mb-8 shadow-lg`}>
                        <product.icon weight="fill" className="w-10 h-10 text-white" />
                      </div>
                      
                      <h3 className="text-white font-display text-2xl text-center mb-8">{product.name}</h3>
                      
                      {/* Benefits */}
                      <div className="grid grid-cols-2 gap-4">
                        {product.benefits.map((benefit, i) => (
                          <div 
                            key={i} 
                            className="bg-white/5 backdrop-blur-sm rounded-xl p-4 text-center border border-white/10"
                          >
                            <benefit.icon weight="fill" className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                            <span className="text-white/80 text-sm font-medium">{benefit.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Systems Section */}
      <section className="section-padding bg-slate-50" data-testid="custom-systems">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-accent mb-6">
                <Wrench weight="fill" className="w-4 h-4 mr-2" />
                Custom Development
              </span>
              
              <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-4 tracking-tight">
                {customSystems.title}
              </h2>
              <p className="text-xl text-cyan-600 font-medium mb-4">
                {customSystems.tagline}
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-10">
                {customSystems.description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {customSystems.capabilities.map((capability, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check weight="bold" className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-slate-700">{capability}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/schedule-demo"
                className="btn-accent inline-flex items-center gap-2"
                data-testid="custom-systems-cta"
              >
                Discuss Your Custom Project
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="relative rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10" />
                
                <div className="absolute top-6 right-6 w-24 h-24 border border-cyan-500/20 rounded-full" />
                <div className="absolute bottom-6 left-6 w-32 h-32 border border-cyan-500/20 rounded-full" />
                
                <div className="relative p-8 md:p-12">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mb-8">
                    <Wrench weight="fill" className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-display text-2xl text-white mb-8">Our Custom Development Process</h3>
                  <div className="space-y-6">
                    {[
                      { step: "1", title: "Discovery", desc: "Deep-dive into your workflows and challenges" },
                      { step: "2", title: "Design", desc: "Prototype solutions tailored to your needs" },
                      { step: "3", title: "Build", desc: "Develop with continuous feedback loops" },
                      { step: "4", title: "Deploy", desc: "Launch with training and ongoing support" }
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                          <span className="font-bold text-white">{item.step}</span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-white">{item.title}</h4>
                          <p className="text-slate-400 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integration Benefits */}
      <section className="section-padding bg-white" data-testid="integration-benefits">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">Why Choose Our Products</span>
            <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-6 tracking-tight">
              Built for Hospice, By Hospice Experts
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our products are designed with deep understanding of CMS CoPs, CHAP standards, 
              and the real-world challenges hospice organizations face every day.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {integrationBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="bg-slate-50 rounded-2xl p-6 border border-slate-100 text-center hover:border-cyan-500/30 hover:shadow-xl transition-all duration-300 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 group-hover:from-cyan-500 group-hover:to-blue-500 flex items-center justify-center mx-auto mb-4 transition-all">
                  <benefit.icon weight="duotone" className="w-7 h-7 text-cyan-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display font-bold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-slate-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Dark Futuristic */}
      <section className="cta-section section-padding" data-testid="products-cta">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-32 h-32 border border-cyan-500/30 rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-cyan-500/30 rounded-full" />
        </div>
        
        <motion.div 
          className="absolute top-0 right-1/4 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="relative max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mx-auto mb-8">
              <Sparkle weight="fill" className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white mb-8 tracking-tight">
              Ready to Transform Your Operations?
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-12">
              Schedule a personalized demo to see how our products and custom solutions 
              can streamline your operations, improve compliance, and drive organizational growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/schedule-demo"
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/30 hover:-translate-y-1 inline-flex items-center justify-center gap-2"
                data-testid="products-cta-demo"
              >
                <Desktop weight="fill" className="w-5 h-5" />
                Schedule a Demo
              </Link>
              <Link
                to="/services"
                className="border-2 border-slate-600 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:bg-slate-800 hover:border-slate-500 inline-flex items-center justify-center gap-2"
              >
                View All Services
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
